import { mayRecordConsumption, membershipStatus } from '../utils/membershipPeriod';
import React, { useState, useMemo, useEffect } from 'react';
import {
  CalendarDaysIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  UserGroupIcon,
  ChartBarIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  CheckIcon,
  BoltIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import { ModalShell, ModalActions, ModalSection } from '../components/modals/core';

const XLSX = require('xlsx');
const jsPDF = require('jspdf').jsPDF;
require('jspdf-autotable');

// Extend jsPDF type
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface Member {
  geloescht?: number;
  austritt_am?: string | null;
  geloescht_am?: string | null;
  mg_nr: string;
  vorname: string;
  name: string;
  email?: string;
}

interface Event {
  id: number;
  beschriftung: string;
  abrechnungsmonat?: string | null;
}

interface Drink {
  id?: number;
  member_id: string;
  month: number;
  amount: number;
  paid: number;
  paid_date?: string;
  payment_method?: string;
}

const DrinksManagement: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnpaidOnly, setFilterUnpaidOnly] = useState(false);
  const [filterWithDrinksOnly, setFilterWithDrinksOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'amount'>('name');
  const [selectedYear, setSelectedYear] = useState<number | 'all'>(() => new Date().getFullYear());
  const [quickAddMode, setQuickAddMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  
  // Modals
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventMonth, setNewEventMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<{member: string, event: number} | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'paypal' | 'bank_transfer'>('cash');
  
  // Editing
  const [editingCell, setEditingCell] = useState<{member: string, event: number} | null>(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersRes, eventsRes, drinksRes] = await Promise.all([
        apiClient.get('/api/members?includeWithDrinks=1'),
        apiClient.get('/api/events'),
        apiClient.get('/api/drinks'),
      ]);

      setMembers(membersRes.data || []);
      setEvents(eventsRes.data || []);
      setDrinks(drinksRes.data || []);
    } catch (err: any) {
      setMessage({ 
        type: 'error', 
        text: err.message || 'Fehler beim Laden der Daten.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Create payments map for quick lookup
  const paymentsMap = useMemo(() => {
    const map: Record<string, Record<number, Drink>> = {};
    drinks.forEach(drink => {
      if (!map[drink.member_id]) map[drink.member_id] = {};
      map[drink.member_id][drink.month] = drink;
    });
    return map;
  }, [drinks]);

  const availableYears = useMemo(() => Array.from(new Set(
    events
      .map(event => event.abrechnungsmonat ? new Date(event.abrechnungsmonat).getFullYear() : null)
      .filter((year): year is number => year !== null && !Number.isNaN(year))
  )).sort((a, b) => b - a), [events]);

  const filteredEvents = useMemo(() => events
    .filter(event => {
      if (selectedYear === 'all') return true;
      if (!event.abrechnungsmonat) return false;
      return new Date(event.abrechnungsmonat).getFullYear() === selectedYear;
    })
    .sort((a, b) => {
      const aDate = a.abrechnungsmonat ? new Date(a.abrechnungsmonat).getTime() : 0;
      const bDate = b.abrechnungsmonat ? new Date(b.abrechnungsmonat).getTime() : 0;
      return bDate - aDate || b.id - a.id;
    }), [events, selectedYear]);

  const filteredEventIds = useMemo(() => new Set(filteredEvents.map(event => event.id)), [filteredEvents]);
  const yearDrinks = useMemo(() => drinks.filter(drink => filteredEventIds.has(drink.month)), [drinks, filteredEventIds]);

  // Filter and sort members
  const filteredMembers = useMemo(() => {
    let filtered = members.filter(member => {
      if (member.geloescht && !yearDrinks.some(drink => String(drink.member_id) === String(member.mg_nr))) return false;
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const fullName = `${member.vorname} ${member.name}`.toLowerCase();
        if (!fullName.includes(search) && !String(member.mg_nr).includes(search)) {
          return false;
        }
      }
      
      // Filter for members with drinks
      if (filterWithDrinksOnly) {
        const hasDrinks = yearDrinks.some(d => d.member_id === member.mg_nr && d.amount > 0);
        if (!hasDrinks) return false;
      }
      
      // Filter for unpaid only
      if (filterUnpaidOnly) {
        const hasUnpaid = yearDrinks.some(d => d.member_id === member.mg_nr && !d.paid && d.amount > 0);
        if (!hasUnpaid) return false;
      }
      
      return true;
    });

    // Sort
    if (sortBy === 'amount') {
      filtered.sort((a, b) => {
        const aTotal = yearDrinks
          .filter(d => d.member_id === a.mg_nr && !d.paid)
          .reduce((sum, d) => sum + d.amount, 0);
        const bTotal = yearDrinks
          .filter(d => d.member_id === b.mg_nr && !d.paid)
          .reduce((sum, d) => sum + d.amount, 0);
        return bTotal - aTotal;
      });
    } else {
      filtered.sort((a, b) => {
        const aName = `${a.vorname} ${a.name}`;
        const bName = `${b.vorname} ${b.name}`;
        return aName.localeCompare(bName);
      });
    }

    return filtered;
  }, [members, yearDrinks, searchTerm, filterUnpaidOnly, filterWithDrinksOnly, sortBy]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalUnpaid = yearDrinks
      .filter(d => !d.paid)
      .reduce((sum, d) => sum + d.amount, 0);
    
    const totalPaid = yearDrinks
      .filter(d => d.paid)
      .reduce((sum, d) => sum + d.amount, 0);
    
    const membersWithDrinks = new Set(
      yearDrinks.filter(d => d.amount > 0).map(d => d.member_id)
    ).size;
    
    const avgPerPerson = membersWithDrinks > 0 
      ? (totalUnpaid + totalPaid) / membersWithDrinks 
      : 0;
    
    const avgPerEvent = filteredEvents.length > 0
      ? (totalUnpaid + totalPaid) / filteredEvents.length
      : 0;

    return {
      totalUnpaid,
      totalPaid,
      membersWithDrinks,
      avgPerPerson,
      avgPerEvent,
    };
  }, [yearDrinks, filteredEvents]);

  // Event handlers
  const handleCreateEvent = async () => {
    if (!newEventTitle.trim()) return;
    
    try {
      const response = await apiClient.post('/api/events', {
        beschriftung: newEventTitle,
        abrechnungsmonat: `${newEventMonth}-01`,
      });
      
      if (response.data) {
        await fetchData();
        setNewEventTitle('');
        setEventModalOpen(false);
        setMessage({ type: 'success', text: 'Event erfolgreich erstellt!' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Erstellen des Events.' });
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    if (!window.confirm('Möchten Sie dieses Event wirklich löschen? Alle zugehörigen Einträge werden ebenfalls gelöscht.')) {
      return;
    }
    
    try {
      await apiClient.delete(`/api/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
      setDrinks(drinks.filter(d => d.month !== eventId));
      setMessage({ type: 'success', text: 'Event erfolgreich gelöscht!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Löschen des Events.' });
    }
  };

  const handleCellEdit = (memberId: string, eventId: number) => {
    const payment = paymentsMap[memberId]?.[eventId];
    setEditingCell({ member: memberId, event: eventId });
    setEditValue(payment?.amount?.toString() || '');
  };

  const handleCellSave = async () => {
    if (!editingCell) return;
    
    const amount = parseFloat(editValue) || 0;
    const payment = paymentsMap[editingCell.member]?.[editingCell.event];
    
    try {
      if (payment?.id) {
        // Update existing
        await apiClient.put(`/api/drinks/${payment.id}`, { amount });
        setDrinks(drinks.map(d => 
          d.id === payment.id ? { ...d, amount } : d
        ));
      } else if (amount > 0) {
        // Create new
        const response = await apiClient.post('/api/drinks', {
          member_id: editingCell.member,
          month: editingCell.event,
          amount,
          paid: 0,
        });
        if (response.data) {
          setDrinks([...drinks, response.data]);
        }
      }
      
      setEditingCell(null);
      setEditValue('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Speichern.' });
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedPayment) return;
    
    const payment = paymentsMap[selectedPayment.member]?.[selectedPayment.event];
    if (!payment?.id) return;
    
    try {
      await apiClient.put(`/api/drinks/${payment.id}`, {
        paid: 1,
        paid_date: new Date().toISOString(),
        payment_method: paymentMethod,
      });
      
      setDrinks(drinks.map(d => 
        d.id === payment.id 
          ? { ...d, paid: 1, paid_date: new Date().toISOString(), payment_method: paymentMethod }
          : d
      ));
      
      setPaymentModalOpen(false);
      setSelectedPayment(null);
      setMessage({ type: 'success', text: 'Zahlung erfolgreich markiert!' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Markieren der Zahlung.' });
    }
  };

  const handleQuickAdd = async (memberId: string) => {
    if (!selectedEvent) return;
    
    const payment = paymentsMap[memberId]?.[selectedEvent];
    const newAmount = payment?.amount === 5 ? 0 : 5;
    
    try {
      if (payment?.id) {
        // Update existing
        await apiClient.put(`/api/drinks/${payment.id}`, { amount: newAmount });
        setDrinks(drinks.map(d => 
          d.id === payment.id ? { ...d, amount: newAmount } : d
        ));
      } else if (newAmount > 0) {
        // Create new
        const response = await apiClient.post('/api/drinks', {
          member_id: memberId,
          month: selectedEvent,
          amount: newAmount,
          paid: 0,
        });
        if (response.data) {
          setDrinks([...drinks, response.data]);
        }
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Fehler beim Speichern.' });
    }
  };

  // Export functions
  const exportToExcel = () => {
    const data: any[][] = [];
    
    // Header row
    const header = ['Name'];
    filteredEvents.forEach(event => header.push(`${event.beschriftung} (${formatEventDate(event)})`));
    header.push('Gesamt Unbezahlt');
    data.push(header);
    
    // Data rows
    filteredMembers.forEach(member => {
      const row = [getMemberName(member.mg_nr)];
      
      filteredEvents.forEach(event => {
        const payment = paymentsMap[member.mg_nr]?.[event.id];
        if (payment && payment.amount > 0) {
          row.push(payment.paid ? `${payment.amount} ✓` : String(payment.amount));
        } else {
          row.push('');
        }
      });
      
      const unpaidTotal = yearDrinks
        .filter(d => d.member_id === member.mg_nr && !d.paid)
        .reduce((sum, d) => sum + d.amount, 0);
      row.push(unpaidTotal > 0 ? String(unpaidTotal) : '');
      
      data.push(row);
    });
    
    // Create workbook
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Verzehrkosten');
    
    // Save file
    XLSX.writeFile(wb, `Verzehrkosten_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setMessage({ type: 'success', text: 'Excel-Export erfolgreich!' });
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(16);
    doc.text('Verzehrkosten Übersicht', 14, 15);
    doc.setFontSize(10);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 14, 22);
    
    // Prepare table data
    const tableColumn = ['Name', ...filteredEvents.map(e => `${e.beschriftung} (${formatEventDate(e)})`), 'Unbezahlt'];
    const tableRows: any[][] = [];
    
    filteredMembers.forEach(member => {
      const row = [getMemberName(member.mg_nr)];
      
      filteredEvents.forEach(event => {
        const payment = paymentsMap[member.mg_nr]?.[event.id];
        if (payment && payment.amount > 0) {
          row.push(payment.paid ? `${payment.amount} €` : `${payment.amount} €`);
        } else {
          row.push('');
        }
      });
      
      const unpaidTotal = yearDrinks
        .filter(d => d.member_id === member.mg_nr && !d.paid)
        .reduce((sum, d) => sum + d.amount, 0);
      row.push(unpaidTotal > 0 ? `${unpaidTotal} €` : '');
      
      tableRows.push(row);
    });
    
    // Add table
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [220, 38, 38] },
    });
    
    // Save file
    doc.save(`Verzehrkosten_${new Date().toISOString().split('T')[0]}.pdf`);
    
    setMessage({ type: 'success', text: 'PDF-Export erfolgreich!' });
  };

  // Helper functions
  const getMemberName = (mg_nr: string) => {
    const member = members.find(m => m.mg_nr === mg_nr);
    return member ? `${member.vorname} ${member.name}` : mg_nr;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatEventDate = (event: Event) => {
    if (!event.abrechnungsmonat) return 'Ohne Abrechnungsdatum';
    return new Intl.DateTimeFormat('de-DE', { month: 'short', year: 'numeric' })
      .format(new Date(event.abrechnungsmonat));
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-52 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1380px] space-y-5 pb-8">
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="flex flex-col gap-5 p-5 sm:p-7 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <ShoppingCartIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Abrechnung</p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-[28px]">Verzehrkosten</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filteredEvents.length} Veranstaltungen · {filteredMembers.length} Mitglieder in der Ansicht
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={exportToExcel} className="flex h-11 items-center gap-2 rounded-xl bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600" title="Excel exportieren">
              <TableCellsIcon className="h-5 w-5" /><span className="hidden sm:inline">Excel</span>
            </button>
            <button onClick={exportToPDF} className="flex h-11 items-center gap-2 rounded-xl bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600" title="PDF exportieren">
              <DocumentArrowDownIcon className="h-5 w-5" /><span className="hidden sm:inline">PDF</span>
            </button>
            <button onClick={fetchData} className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600" title="Aktualisieren">
              <ArrowPathIcon className="h-5 w-5" />
            </button>
            <button onClick={() => setEventModalOpen(true)} className="flex h-11 items-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-bold text-white transition-colors hover:bg-primary-700">
              <PlusCircleIcon className="h-5 w-5" /> Event
            </button>
          </div>
        </div>
      </section>

      <StatGroup columns={4}>
        <StatCard icon={ExclamationTriangleIcon} tone="error" valueTone="error" label="Offen" value={formatCurrency(stats.totalUnpaid)} />
        <StatCard icon={CheckCircleIcon} tone="success" valueTone="success" label="Bezahlt" value={formatCurrency(stats.totalPaid)} />
        <StatCard icon={UserGroupIcon} tone="primary" label="Mit Verzehr" value={String(stats.membersWithDrinks)} />
        <StatCard icon={ChartBarIcon} tone="neutral" label="Ø pro Person" value={formatCurrency(stats.avgPerPerson)} />
      </StatGroup>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_auto] lg:items-center">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Name oder Mitgliedsnummer suchen" className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-4 text-sm text-gray-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AttributeLabel iconKey="year" htmlFor="drinks-year" className="sr-only">Abrechnungsjahr</AttributeLabel>
            <select id="drinks-year" value={selectedYear} onChange={(e) => { setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value)); setSelectedEvent(null); }} className="h-11 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-800 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              <option value="all">Alle Jahre</option>
              {availableYears.map(year => <option key={year} value={year}>Abrechnungsjahr {year}</option>)}
            </select>
            <button onClick={() => setFilterUnpaidOnly(!filterUnpaidOnly)} className={`flex h-11 items-center gap-2 rounded-xl px-3 text-sm font-semibold transition-colors ${filterUnpaidOnly ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`}>
              <FunnelIcon className="h-4 w-4" /> Nur offen
            </button>
            <button onClick={() => setFilterWithDrinksOnly(!filterWithDrinksOnly)} className={`flex h-11 items-center gap-2 rounded-xl px-3 text-sm font-semibold transition-colors ${filterWithDrinksOnly ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`}>
              <UserGroupIcon className="h-4 w-4" /> Mit Betrag
            </button>
            <button onClick={() => setSortBy(sortBy === 'name' ? 'amount' : 'name')} className="flex h-11 items-center gap-2 rounded-xl bg-gray-100 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
              <ChartBarIcon className="h-4 w-4" /> {sortBy === 'name' ? 'Name' : 'Offener Betrag'}
            </button>
            <button onClick={() => { setQuickAddMode(!quickAddMode); setSelectedEvent(null); }} className={`flex h-11 items-center gap-2 rounded-xl px-3 text-sm font-semibold transition-colors ${quickAddMode ? 'bg-warning-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`}>
              <BoltIcon className="h-4 w-4" /> Schnelleingabe
            </button>
            {quickAddMode && (
              <select value={selectedEvent || ''} onChange={(e) => setSelectedEvent(Number(e.target.value) || null)} className="h-11 min-w-[190px] rounded-xl border border-warning-400 bg-warning-50 px-3 text-sm font-semibold text-gray-800 focus:ring-warning-500 dark:border-warning-700 dark:bg-warning-900/20 dark:text-white">
                <option value="">Veranstaltung wählen</option>
                {filteredEvents.map(event => <option key={event.id} value={event.id}>{event.beschriftung} · {formatEventDate(event)}</option>)}
              </select>
            )}
          </div>
        </div>
      </section>

      {message.text && (
        <div className={`flex items-center gap-3 rounded-xl border p-4 ${message.type === 'success' ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400' : 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-400'}`}>
          {message.type === 'success' ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
          <span className="flex-1 text-sm font-semibold">{message.text}</span>
          <button onClick={() => setMessage({ type: '', text: '' })} className="rounded-lg p-1 hover:bg-black/5"><XMarkIcon className="h-5 w-5" /></button>
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="flex flex-col gap-2 border-b border-gray-200 px-5 py-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-bold text-gray-900 dark:text-white">Kostenmatrix {selectedYear === 'all' ? 'aller Jahre' : selectedYear}</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Doppelklick auf einen Betrag zum Bearbeiten · horizontal scrollen für weitere Events</p>
          </div>
          <div className="flex items-center gap-3 text-xs font-semibold text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-success-500" /> Bezahlt</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-error-500" /> Offen</span>
          </div>
        </div>
        <div className="max-h-[68vh] overflow-auto">
          <table className="w-full min-w-max border-separate border-spacing-0">
            <thead className="sticky top-0 z-30">
              <tr>
                <th className="sticky left-0 z-40 min-w-[210px] border-b border-r border-gray-200 bg-gray-50 px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">Mitglied</th>
                {filteredEvents.map(event => (
                  <th key={event.id} className="min-w-[150px] border-b border-r border-gray-200 bg-gray-50 px-3 py-3 text-center dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex items-start justify-center gap-1">
                      <div className="min-w-0">
                        <div className="max-w-[120px] truncate text-xs font-bold text-gray-800 dark:text-gray-100" title={event.beschriftung}>{event.beschriftung}</div>
                        <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400">{formatEventDate(event)}</div>
                      </div>
                      <button onClick={() => handleDeleteEvent(event.id)} className="rounded-lg p-1 text-gray-400 transition hover:bg-error-100 hover:text-error-600 dark:hover:bg-error-900/30" title={`${event.beschriftung} löschen`}><TrashIcon className="h-3.5 w-3.5" /></button>
                    </div>
                  </th>
                ))}
                <th className="sticky right-0 z-40 min-w-[125px] border-b border-l border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">Offen</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map(member => {
                const memberDrinks = yearDrinks.filter(d => d.member_id === member.mg_nr);
                const unpaidTotal = memberDrinks.filter(d => !d.paid).reduce((sum, d) => sum + d.amount, 0);
                const paidTotal = memberDrinks.filter(d => d.paid).reduce((sum, d) => sum + d.amount, 0);
                return (
                  <tr key={member.mg_nr} className="group">
                    <td className="sticky left-0 z-20 border-b border-r border-gray-200 bg-white px-5 py-3 group-hover:bg-gray-50 dark:border-gray-700 dark:bg-[#1c2432] dark:group-hover:bg-gray-800">
                      <div className="font-bold text-gray-900 dark:text-white">{member.vorname} {member.name}{(member.geloescht || member.austritt_am) && <span className="ml-2 text-xs text-warning-700 dark:text-warning-300">{membershipStatus(member)}</span>}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400"><span>#{member.mg_nr}</span>{paidTotal > 0 && <span className="text-success-600 dark:text-success-400">{formatCurrency(paidTotal)} bezahlt</span>}</div>
                    </td>
                    {filteredEvents.map(event => {
                      const payment = paymentsMap[member.mg_nr]?.[event.id];
                      const isEditing = editingCell?.member === member.mg_nr && editingCell?.event === event.id;
                      return (
                        <td key={event.id} className="border-b border-r border-gray-200 px-3 py-2 text-center group-hover:bg-gray-50 dark:border-gray-700 dark:group-hover:bg-gray-800/60">
                          {quickAddMode && selectedEvent === event.id && (payment || mayRecordConsumption(member, event.abrechnungsmonat)) ? (
                            <button onClick={() => handleQuickAdd(member.mg_nr)} className={`min-h-[36px] w-full rounded-xl px-3 text-sm font-bold transition ${payment?.amount === 5 ? (payment.paid ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400' : 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400') : 'bg-warning-100 text-warning-700 hover:bg-warning-200 dark:bg-warning-900/20 dark:text-warning-400'}`}>{payment?.amount === 5 ? '5,00 €' : '+ 5,00 €'}</button>
                          ) : isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') setEditingCell(null); }} className="h-9 w-20 rounded-lg border border-primary-500 bg-white px-2 text-center text-sm font-bold focus:ring-2 focus:ring-primary-500/30 dark:bg-gray-700 dark:text-white" autoFocus step="0.01" />
                              <button onClick={handleCellSave} className="rounded-lg p-2 text-success-600 hover:bg-success-100"><CheckIcon className="h-4 w-4" /></button>
                              <button onClick={() => setEditingCell(null)} className="rounded-lg p-2 text-error-600 hover:bg-error-100"><XMarkIcon className="h-4 w-4" /></button>
                            </div>
                          ) : payment?.amount > 0 ? (
                            <div onDoubleClick={() => handleCellEdit(member.mg_nr, event.id)} className={`mx-auto flex min-h-[38px] max-w-[125px] cursor-pointer items-center justify-center gap-1.5 rounded-xl border px-2 text-sm font-bold ${payment.paid ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400' : 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-400'}`}>
                              <span>{formatCurrency(payment.amount)}</span>
                              {payment.paid ? <CheckCircleIcon className="h-4 w-4" /> : <button onClick={(e) => { e.stopPropagation(); setSelectedPayment({ member: member.mg_nr, event: event.id }); setPaymentModalOpen(true); }} className="rounded-md p-1 text-primary-600 hover:bg-primary-100 dark:text-primary-400" title="Als bezahlt markieren"><CreditCardIcon className="h-4 w-4" /></button>}
                            </div>
                          ) : (
                            <button onClick={() => handleCellEdit(member.mg_nr, event.id)} className="h-9 w-full rounded-xl text-gray-300 transition hover:bg-primary-50 hover:text-primary-600 dark:text-gray-600 dark:hover:bg-primary-900/20" title="Betrag eintragen">—</button>
                          )}
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-20 border-b border-l border-gray-200 bg-white px-4 py-3 text-center group-hover:bg-gray-50 dark:border-gray-700 dark:bg-[#1c2432] dark:group-hover:bg-gray-800">
                      {unpaidTotal > 0 ? <span className="inline-flex rounded-full bg-error-100 px-3 py-1 text-sm font-bold text-error-700 dark:bg-error-900/30 dark:text-error-400">{formatCurrency(unpaidTotal)}</span> : <CheckCircleIcon className="mx-auto h-5 w-5 text-success-500" />}
                    </td>
                  </tr>
                );
              })}
              {filteredMembers.length === 0 && (
                <tr><td colSpan={filteredEvents.length + 2} className="px-6 py-16 text-center"><UserGroupIcon className="mx-auto h-9 w-9 text-gray-300" /><p className="mt-3 font-bold text-gray-700 dark:text-gray-200">Keine Mitglieder für diese Filter</p><p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Passe Suche, Jahr oder Statusfilter an.</p></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Event Modal */}
      <ModalShell
        open={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        variant="form"
        size="sm"
        icon={CalendarDaysIcon}
        title="Neues Event erstellen"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: () => setEventModalOpen(false) }}
            primary={{
              label: 'Erstellen',
              onClick: handleCreateEvent,
              disabled: !newEventTitle.trim() || !newEventMonth,
              icon: <PlusCircleIcon className="h-5 w-5" />,
            }}
          />
        }
      >
        <ModalSection>
          <div className="space-y-4">
            <div>
              <div className="mb-2">
                <AttributeLabel iconKey="eventTitle" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Event-Name
                </AttributeLabel>
              </div>
              <input
                type="text"
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                placeholder="z.B. Vereinsabend März"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                autoFocus
              />
            </div>
            <div>
              <div className="mb-2">
                <AttributeLabel iconKey="date" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Abrechnungsmonat
                </AttributeLabel>
              </div>
              <input
                type="month"
                value={newEventMonth}
                onChange={(e) => setNewEventMonth(e.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Monat und Jahr erscheinen gemeinsam in der Tabellenüberschrift.</p>
            </div>
          </div>
        </ModalSection>
      </ModalShell>

      {/* Payment Modal */}
      {selectedPayment && (
        <ModalShell
          open={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedPayment(null);
          }}
          variant="form"
          size="sm"
          icon={CreditCardIcon}
          title="Zahlung markieren"
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{
                label: 'Abbrechen',
                onClick: () => {
                  setPaymentModalOpen(false);
                  setSelectedPayment(null);
                },
              }}
              primary={{
                label: 'Als bezahlt markieren',
                onClick: handleMarkPaid,
                icon: <CheckCircleIcon className="h-5 w-5" />,
              }}
            />
          }
        >
          <ModalSection>
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="mb-1"><AttributeLabel iconKey="member" textClassName="text-sm text-gray-600 dark:text-gray-400">Mitglied</AttributeLabel></p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {getMemberName(selectedPayment.member)}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="mb-1"><AttributeLabel iconKey="amount" textClassName="text-sm text-gray-600 dark:text-gray-400">Betrag</AttributeLabel></p>
                <p className="text-2xl font-bold text-primary-600">
                  {formatCurrency(paymentsMap[selectedPayment.member]?.[selectedPayment.event]?.amount || 0)}
                </p>
              </div>

              <div>
                <div className="mb-2">
                  <AttributeLabel iconKey="payment" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Zahlungsmethode
                  </AttributeLabel>
                </div>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as any)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="cash">Bar</option>
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Überweisung</option>
                </select>
              </div>
            </div>
          </ModalSection>
        </ModalShell>
      )}
    </div>
  );
};

export default DrinksManagement;
